import { BlocksViewLink, BlocksViewNode } from '../types';
import { astar, Cell } from '../misc/astar';
import { BlockGrid, Direction } from './blockGrid';
import { RoutingInfo, Waypoint } from './types';
import { routeMaze } from './maze';
import { stripColinearPoints } from '../misc/geometry';

const isInAndOutRouting = true;

function last<T>(array: T[]) {
  return array[array.length - 1];
}

const ltrbCost = (pt: Cell, target: BlocksViewNode) => {
  // cost of of cell to any edge of target

  const left = target.col - 1;
  const top = target.row - 1;
  const right = target.col + target.colSpan;
  const bottom = target.row + target.rowSpan;

  const h = Math.max(left - pt.col, 0) + Math.max(pt.col - right, 0);
  const v = Math.max(top - pt.row, 0) + Math.max(pt.row - bottom, 0);
  const diag =
    (pt.col <= left && (pt.row <= top || pt.row >= bottom)) ||
    (pt.col >= right && (pt.row <= top || pt.row >= bottom));

  return h + v + (diag ? 1 : 0);
};

const smartPath = (
  blockGrid: BlockGrid, // block grid for the lca
  source: BlocksViewNode,
  sourceEdges: Direction[],
  targetScore: (k: Cell) => number
) => {
  /* prepare seeds and get the solution */

  const seeds = [];

  const isValid = (p: Cell) =>
    blockGrid.isInside(p.col, p.row) && !blockGrid.get(p.col, p.row);

  for (const edge of sourceEdges) {
    switch (edge) {
      case Direction.Left:
        for (let row = source.row; row < source.row + source.rowSpan; ++row) {
          seeds.push({ col: source.col - 1, row: row });
        }
        break;

      case Direction.Up:
        for (let col = source.col; col < source.col + source.colSpan; ++col) {
          seeds.push({ col: col, row: source.row - 1 });
        }
        break;

      case Direction.Right:
        for (let row = source.row; row < source.row + source.rowSpan; ++row) {
          seeds.push({ col: source.col + source.colSpan, row: row });
        }
        break;

      case Direction.Down:
        for (let col = source.col; col < source.col + source.colSpan; ++col) {
          seeds.push({ col: col, row: source.row + source.rowSpan });
        }
        break;
    }
  }

  const path = astar(seeds, isValid, targetScore);

  /* strip colinear points into ret */
  return stripColinearPoints(
    path,
    pt => pt.col,
    pt => pt.row
  ).reverse(); // astar returns points from target to source
};

const routeAcross = (
  from: BlocksViewNode,
  fromDirections: Direction[],
  h: (pt: Cell) => number,
  _link: BlocksViewLink,
  getRoutingInfo: (node: BlocksViewNode) => RoutingInfo
) => {
  const lca = from.parent!;
  const routingInfo = getRoutingInfo(lca);
  const path = smartPath(routingInfo.blockGrid, from, fromDirections, h);
  const waypoints: Waypoint[] = [];

  if (path) {
    for (const point of path) {
      const wp: Waypoint = {
        colRef: lca,
        col: point.col,
        μcol: NaN,
        rowRef: lca,
        row: point.row,
        μrow: NaN,
      };

      waypoints.push(wp);
    }
  }

  return waypoints;
};

const isHorizontal = (direction: Direction): boolean =>
  direction === Direction.Left || direction === Direction.Right;

export const getRowRef = (waypoint: Waypoint): BlocksViewNode =>
  waypoint.rowRef ?? waypoint.colRef;

const sameRow = (a: Waypoint, b: Waypoint): boolean =>
  getRowRef(a) === getRowRef(b) && a.row === b.row;

const sameCol = (a: Waypoint, b: Waypoint): boolean =>
  a.colRef === b.colRef && a.col === b.col;

/**
 * Rewrites, in place, the waypoints of the straight run at one end of `run` onto `onto`'s row (or
 * column), so a route that leaves or enters a group keeps its height instead of jogging to the
 * middle of the wider outer track. Only for a group in a single track, which the inner height lies
 * within. A run that never turns moves whole, so only when its other end is joined afterwards.
 * Returns whether it moved anything.
 */
const carryStraightInPlace = (
  run: Waypoint[],
  onto: Waypoint,
  through: BlocksViewNode,
  direction: Direction,
  carryWhole: boolean
): boolean => {
  if (run.length === 0) return false;
  const first = run[0];
  const horizontal = isHorizontal(direction);
  const turn = run.findIndex(wp =>
    horizontal ? !sameRow(wp, first) : !sameCol(wp, first)
  );
  if (turn === -1 && !carryWhole) return false;
  const end = turn === -1 ? run.length : turn;
  if (horizontal) {
    if (through.rowSpan !== 1 || first.row !== through.row) return false;
    run.slice(0, end).forEach(wp => {
      wp.rowRef = getRowRef(onto);
      wp.row = onto.row;
      wp.μrow = onto.μrow;
    });
    return true;
  }
  if (through.colSpan !== 1 || first.col !== through.col) return false;
  run.slice(0, end).forEach(wp => {
    wp.colRef = onto.colRef;
    wp.col = onto.col;
    wp.μcol = onto.μcol;
  });
  return true;
};

const spliceOut = (
  from: Waypoint[],
  to: Waypoint[],
  direction: Direction,
  through: BlocksViewNode,
  carryWhole: boolean
): Waypoint[] => {
  const a = from[from.length - 1];
  const b = to[0];

  if (carryStraightInPlace(to, a, through, direction, carryWhole)) return [];

  const horizontal = () => {
    const colRef = b.colRef;
    const col = b.col;
    const μcol = b.μcol;

    return [
      {
        colRef: colRef,
        col: col,
        μcol: μcol,

        rowRef: a.rowRef ?? a.colRef,
        row: a.row,
        μrow: a.μrow,
      },
      {
        colRef: colRef,
        col: col,
        μcol: μcol,

        rowRef: b.rowRef ?? b.colRef,
        row: b.row,
        μrow: b.μrow,
      },
    ];
  };

  const vertical = () => {
    const rowRef = b.rowRef ?? b.colRef;
    const row = b.row;
    const μrow = b.μrow;

    return [
      {
        colRef: a.colRef,
        col: a.col,
        μcol: a.μcol,

        rowRef: rowRef,
        row: row,
        μrow: μrow,
      },
      {
        colRef: b.colRef,
        col: b.col,
        μcol: b.μcol,

        rowRef: rowRef,
        row: row,
        μrow: μrow,
      },
    ];
  };

  switch (direction) {
    case Direction.Left:
    case Direction.Right:
      return horizontal();

    case Direction.Up:
    case Direction.Down:
      return vertical();
  }

  return [];
};

const spliceIn = (
  from: Waypoint[],
  to: Waypoint[],
  direction: Direction,
  through: BlocksViewNode
): Waypoint[] => {
  const a = last(from);
  const b = to[0];

  if (carryStraightInPlace([...from].reverse(), b, through, direction, false)) {
    return [];
  }

  const horizontal = () => {
    const colRef = a.colRef;
    const col = a.col;
    const μcol = a.μcol;

    return [
      {
        colRef: colRef,
        col: col,
        μcol: μcol,

        rowRef: a.rowRef ?? a.colRef,
        row: a.row,
        μrow: a.μrow,
      },
      {
        colRef: colRef,
        col: col,
        μcol: μcol,

        rowRef: b.rowRef ?? b.colRef,
        row: b.row,
        μrow: b.μrow,
      },
    ];
  };

  const vertical = () => {
    const rowRef = a.rowRef ?? a.colRef;
    const row = a.row;
    const μrow = a.μrow;

    return [
      {
        colRef: a.colRef,
        col: a.col,
        μcol: a.μcol,

        rowRef: rowRef,
        row: row,
        μrow: μrow,
      },
      {
        colRef: b.colRef,
        col: b.col,
        μcol: b.μcol,

        rowRef: rowRef,
        row: row,
        μrow: μrow,
      },
    ];
  };

  switch (direction) {
    case Direction.Left:
    case Direction.Right:
      return horizontal();

    case Direction.Up:
    case Direction.Down:
      return vertical();
  }

  return [];
};

const routeOut = (
  from: BlocksViewNode,
  to: BlocksViewNode,
  direction: Direction,
  _link: BlocksViewLink,
  getRoutingInfo: (node: BlocksViewNode) => RoutingInfo,
  isFarEndSpliced: boolean
) => {
  const h = (p: Cell, n: BlocksViewNode) => {
    // get cost of p to the specified inside edge

    switch (direction) {
      case Direction.Left:
        return p.col;

      case Direction.Up:
        return p.row;

      case Direction.Right:
        return n.colSize!.length - 1 - p.col;

      case Direction.Down:
        return n.rowSize!.length - 1 - p.row;
    }

    return 0;
  };

  const route: Waypoint[] = [];

  if (from === to) {
    return route;
  }

  let a = from;

  let exitEdge = [
    Direction.Up,
    Direction.Left,
    Direction.Right,
    Direction.Down,
  ];

  while (a.parent && a !== to) {
    /* route through a's parent from a to a's parent */
    /* on the first iteration, I can leave any edge */

    const routingInfo = getRoutingInfo(a.parent);
    const blockGrid = routingInfo.blockGrid;
    const path = smartPath(blockGrid, a, exitEdge, p => h(p, a.parent!));

    exitEdge = [direction];

    /* get path waypoints */

    const tmp: Waypoint[] = [];

    for (const pt of path) {
      const wp = {
        colRef: a.parent,
        col: pt.col,
        μcol: NaN,
        rowRef: a.parent,
        row: pt.row,
        μrow: NaN,
      };

      tmp.push(wp);
    }

    /* splice from current route to new bit of path */

    if (route.length !== 0 && tmp.length !== 0) {
      const spl = spliceOut(
        route,
        tmp,
        direction,
        a,
        a.parent !== to || isFarEndSpliced
      );
      route.push(...spl);
    }

    /* add path to route */

    route.push(...tmp);

    a = a.parent;
  }

  return route;
};

// this finds out which inside edge I got routed from
const getOutsideEdge = (p: Cell, n: BlocksViewNode) => {
  if (p.col === n.col - 1) return Direction.Left;
  if (p.row === n.row - 1) return Direction.Up;
  if (p.col === n.col + n.colSpan) return Direction.Right;
  if (p.row === n.row + n.rowSpan) return Direction.Down;

  return 0;
};

const getInsideEndpoint = (
  node: BlocksViewNode,
  pt: Cell,
  _link: BlocksViewLink,
  _routingInfo: RoutingInfo
) => {
  const left = 0;
  const up = 0;
  const right = node.colSize!.length - 1;
  const down = node.rowSize!.length - 1;

  if (pt.col === left) {
    return {
      colRef: node,
      col: left,
      μcol: -Infinity,
      rowRef: node,
      row: pt.row,
      μrow: NaN,
    };
  }

  if (pt.row === up) {
    return {
      colRef: node,
      col: pt.col,
      μcol: NaN,
      rowRef: node,
      row: up,
      μrow: -Infinity,
    };
  }

  if (pt.col === right) {
    return {
      colRef: node,
      col: right,
      μcol: +Infinity,
      rowRef: node,
      row: pt.row,
      μrow: NaN,
    };
  }

  // must be bottom edge

  return {
    colRef: node,
    col: pt.col,
    μcol: NaN,
    rowRef: node,
    row: down,
    μrow: +Infinity,
  };
};

const getOutsideEndpoint = (
  node: BlocksViewNode,
  wp: Waypoint,
  _link: BlocksViewLink,
  _routingInfo: RoutingInfo
): Waypoint => {
  const lca = node.parent!;
  if (wp.col === node.col - 1) {
    /* left edge */
    return {
      colRef: lca,
      col: node.col,
      μcol: -Infinity,
      rowRef: lca,
      row: wp.row,
      μrow: NaN,
    };
  }

  if (wp.row === node.row - 1) {
    /* top edge */
    return {
      colRef: lca,
      col: wp.col,
      μcol: NaN,
      rowRef: lca,
      row: node.row,
      μrow: -Infinity,
    };
  }

  if (wp.col === node.col + node.colSpan) {
    /* right edge */
    return {
      colRef: lca,
      col: node.col + node.colSpan - 1,
      μcol: +Infinity,
      rowRef: lca,
      row: wp.row,
      μrow: NaN,
    };
  }

  if (wp.row === node.row + node.rowSpan) {
    /* bottom edge */
    return {
      colRef: lca,
      col: wp.col,
      μcol: NaN,
      rowRef: lca,
      row: node.row + node.rowSpan - 1,
      μrow: +Infinity,
    };
  }

  return {
    colRef: lca,
    col: node.col,
    μcol: 0,
    rowRef: lca,
    row: node.row,
    μrow: 0,
  };
};

export const routeSelf = (
  _link: BlocksViewLink,
  node: BlocksViewNode,
  _getRoutingInfo: (node: BlocksViewNode) => RoutingInfo
) => {
  const wp: Waypoint[] = [];
  const lca = node.parent!;

  let col = node.col;
  let μcol = NaN;

  let row = node.row + node.rowSpan;
  let μrow = -Infinity;
  wp.push({ colRef: lca, col: col, μcol: μcol, row: row, μrow: μrow }); // a

  row = node.row + node.rowSpan;
  μrow = NaN;
  wp.push({ colRef: lca, col: col, μcol: μcol, row: row, μrow: μrow }); // b

  col = node.col - 1;
  μcol = NaN;
  wp.push({ colRef: lca, col: col, μcol: μcol, row: row, μrow: μrow }); // c

  row = node.row + node.rowSpan - 1;
  μrow = NaN;
  wp.push({ colRef: lca, col: col, μcol: μcol, row: row, μrow: μrow }); // d

  col = node.col - 1;
  μcol = +Infinity;
  wp.push({ colRef: lca, col: col, μcol: μcol, row: row, μrow: μrow }); // e

  return wp;
};

export const routeSiblings = (
  link: BlocksViewLink,
  getRoutingInfo: (node: BlocksViewNode) => RoutingInfo
): Waypoint[] => {
  const source = link.sourceProxy;
  const target = link.targetProxy;

  if (!source || !target) {
    return [];
  }

  const fromEdges = [
    Direction.Left,
    Direction.Up,
    Direction.Right,
    Direction.Down,
  ];

  const lca = source.parent!;
  const ret = routeAcross(
    source,
    fromEdges,
    cell => ltrbCost(cell, target),
    link,
    getRoutingInfo
  );

  if (ret) {
    const routingInfo = getRoutingInfo(lca);
    const bgn = getOutsideEndpoint(source, ret[0], link, routingInfo);
    const end = getOutsideEndpoint(target, last(ret), link, routingInfo);

    ret.unshift(bgn);
    ret.push(end);
  }

  return ret ?? routeMaze(link, getRoutingInfo, lca);
};

const inAndOutRouting = (target: BlocksViewNode, end: Waypoint) => {
  const waypoints: Waypoint[] = [];

  if (end.μcol === -Infinity || end.μcol === +Infinity) {
    const p = target.parent!;
    const col =
      end.μcol === -Infinity ? target.col - 1 : target.col + target.colSpan;
    const μcol = -end.μcol;
    const row = target.row;

    waypoints.push({
      colRef: p,
      col: col,
      μcol: NaN,
      rowRef: target,
      row: end.row,
      μrow: end.μrow,
    });

    waypoints.push({
      colRef: waypoints[0].colRef,
      col: waypoints[0].col,
      μcol: waypoints[0].μcol,
      rowRef: p,
      row: row,
      μrow: NaN,
    });

    waypoints.push({
      colRef: waypoints[0].colRef,
      col: waypoints[0].col,
      μcol: μcol,
      rowRef: waypoints[1].rowRef,
      row: waypoints[1].row,
      μrow: NaN,
    });
  } else {
    const p = target.parent!;
    const col = target.col; // possibly choose the col more carefully
    const row =
      end.μrow === -Infinity ? target.row - 1 : target.row + target.rowSpan;
    const μrow = -end.μrow;

    waypoints.push({
      colRef: end.colRef,
      col: end.col,
      μcol: NaN,
      rowRef: p,
      row: row,
      μrow: NaN,
    });

    waypoints.push({
      colRef: p,
      col: col,
      μcol: NaN,
      rowRef: waypoints[0].rowRef,
      row: waypoints[0].row,
      μrow: NaN,
    });

    waypoints.push({
      colRef: waypoints[1].colRef,
      col: waypoints[1].col,
      μcol: NaN,
      rowRef: p,
      row: row,
      μrow: μrow,
    });
  }

  return waypoints;
};

export const internalRouteAscendant = (
  link: BlocksViewLink,
  source: BlocksViewNode,
  target: BlocksViewNode,
  getRoutingInfo: (node: BlocksViewNode) => RoutingInfo
) => {
  let a = source;

  while (a.parent && a.parent !== target) {
    a = a.parent;
  }

  const left = a.col;
  const up = a.row;
  const right = target.colSize!.length - (a.col + a.colSpan);
  const down = target.rowSize!.length - (a.row + a.rowSpan);
  const min = Math.min(up, left, right, down);

  const direction =
    min === up
      ? Direction.Up
      : min === left
        ? Direction.Left
        : min === right
          ? Direction.Right
          : Direction.Down;

  const waypoints = routeOut(
    source,
    target,
    direction,
    link,
    getRoutingInfo,
    false
  );

  const bgn = getOutsideEndpoint(
    source,
    waypoints[0],
    link,
    getRoutingInfo(source.parent!)
  );
  waypoints.unshift(bgn);

  const end = getInsideEndpoint(
    target,
    last(waypoints),
    link,
    getRoutingInfo(target)
  );

  if (isInAndOutRouting) {
    waypoints.push(...inAndOutRouting(target, end));
  } else {
    waypoints.push(end);
  }

  return waypoints;
};

export const routeAscendant = (
  link: BlocksViewLink,
  getRoutingInfo: (node: BlocksViewNode) => RoutingInfo
) => {
  const source = link.sourceProxy;
  const target = link.targetProxy;

  if (!source || !target) {
    return [];
  }

  return internalRouteAscendant(link, source, target, getRoutingInfo);
};

export const routeDescendant = (
  link: BlocksViewLink,
  getRoutingInfo: (node: BlocksViewNode) => RoutingInfo
) => {
  const source = link.targetProxy;
  const target = link.sourceProxy;

  if (!source || !target) {
    return [];
  }

  return internalRouteAscendant(link, source, target, getRoutingInfo).reverse();
};

export const routeCousins = (
  link: BlocksViewLink,
  getRoutingInfo: (node: BlocksViewNode) => RoutingInfo,
  lca: BlocksViewNode
) => {
  const source = link.sourceProxy;
  const target = link.targetProxy;

  if (!source || !target) {
    return [];
  }

  let a = source;
  let b = target;

  while (a.parent && a.parent !== lca) {
    a = a.parent;
  }

  while (b.parent && b.parent !== lca) {
    b = b.parent;
  }

  const fromDirections = [
    Direction.Up,
    Direction.Left,
    Direction.Right,
    Direction.Down,
  ];

  const cost = (pt: Cell) => ltrbCost(pt, b);

  const ab = routeAcross(a, fromDirections, cost, link, getRoutingInfo);

  if (!ab) {
    return routeMaze(link, getRoutingInfo, lca) ?? [];
  }

  const waypoints = [];

  const aEdge = getOutsideEdge(ab[0], a); // find which edge of a ended up getting used
  const sa = routeOut(source, a, aEdge, link, getRoutingInfo, true);

  const bEdge = getOutsideEdge(last(ab), b); // find which edge b ended up getting used
  const bt = routeOut(target, b, bEdge, link, getRoutingInfo, true).reverse();

  if (sa.length !== 0) {
    const bgn = getOutsideEndpoint(
      source,
      sa[0],
      link,
      getRoutingInfo(source.parent!)
    );
    const spl = spliceOut(sa, ab, aEdge, a, bt.length !== 0);

    waypoints.push(bgn);
    waypoints.push(...sa);
    waypoints.push(...spl);
  } else {
    const bgn = getOutsideEndpoint(
      source,
      ab[0],
      link,
      getRoutingInfo(source.parent!)
    );

    waypoints.push(bgn);
  }

  waypoints.push(...ab);

  if (bt.length !== 0) {
    const spl = spliceIn(ab, bt, bEdge, b);

    const end = getOutsideEndpoint(
      target,
      last(bt),
      link,
      getRoutingInfo(target.parent!)
    );

    waypoints.push(...spl);
    waypoints.push(...bt);
    waypoints.push(end);
  } else {
    const end = getOutsideEndpoint(
      target,
      last(ab),
      link,
      getRoutingInfo(target.parent!)
    );

    waypoints.push(end);
  }

  return waypoints;
};
